import type { ProgramTheme } from '../api/useProgramThemes';
import { Section } from './DetailSection';
import { PassPreview } from './PassPreview';

const images = (theme: ProgramTheme) => [
  { label: 'Icon', uri: theme.icon2x, links: [{ label: '2x', uri: theme.icon2x }, { label: '1x', uri: theme.icon }] },
  { label: 'Logo', uri: theme.logo2x, links: [{ label: '2x', uri: theme.logo2x }, { label: '1x', uri: theme.logo }] },
  { label: 'Banner', uri: theme.strip2x, links: [{ label: '2x', uri: theme.strip2x }, { label: '1x', uri: theme.strip }] },
  ...(theme.hero
    ? [{ label: 'Hero', uri: theme.hero, links: [{ label: 'Original', uri: theme.hero }, { label: 'Preview', uri: theme.hero_preview ?? '' }] }]
    : []),
];

const colors = (theme: ProgramTheme) => [
  { label: 'Background', value: theme.background_color },
  { label: 'Label', value: theme.label_color },
  { label: 'Text', value: theme.text_color },
];

export const ThemeSummary = ({ theme }: { theme: ProgramTheme }) => (
  <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
    <div className="flex min-w-0 flex-col gap-6">
      <Section title="Images">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {images(theme).map((img) => (
            <div key={img.label} className="flex flex-col gap-1.5">
              <span className="text-xs text-gray-400">{img.label}</span>
              <div className="flex h-24 w-40 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                <img src={img.uri} alt={img.label} className="max-h-full max-w-full object-contain" />
              </div>
              <span className="text-xs text-gray-500">
                {img.links
                  .filter((l) => l.uri)
                  .map((l, i) => (
                    <span key={l.label}>
                      {i > 0 && ' · '}
                      <a href={l.uri} target="_blank" rel="noreferrer" className="underline hover:text-gray-900">
                        {l.label}
                      </a>
                    </span>
                  ))}
              </span>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Colours">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {colors(theme).map((c) => (
            <div key={c.label}>
              <dt className="text-xs text-gray-400">{c.label}</dt>
              <dd className="mt-1 flex items-center gap-2 text-sm text-gray-900">
                <span className="h-5 w-5 rounded border border-gray-200" style={{ backgroundColor: `#${c.value}` }} />
                <span className="font-mono">#{c.value.toUpperCase()}</span>
              </dd>
            </div>
          ))}
        </dl>
      </Section>
    </div>
    <Section title="Pass preview">
      <PassPreview
        name={theme.name}
        logo={theme.logo2x}
        strip={theme.strip2x}
        icon={theme.icon2x}
        labelColor={theme.label_color}
        textColor={theme.text_color}
        backgroundColor={theme.background_color}
      />
    </Section>
  </div>
);
